import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Lightbox from './Lightbox';
import useLightbox from '../hooks/useLightbox';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'lodge', label: 'Lodge' },
  { id: 'villa', label: 'Villa' },
  { id: 'beachbar', label: 'Beach Bar & Pool' },
  { id: 'explore', label: 'Explore' },
];

const GALLERY_IMAGES = [
  // Lodge images
  { src: '/bedroom.webp', category: 'lodge', alt: 'Lodge bedroom' },
  { src: '/villa.webp', category: 'lodge', alt: 'Lodge interior' },
  { src: '/terrace.webp', category: 'lodge', alt: 'Lodge terrace' },
  
  // Villa images
  { src: '/terrace.webp', category: 'villa', alt: 'Villa terrace' },
  { src: '/villa.webp', category: 'villa', alt: 'Villa exterior' },
  { src: '/bedroom.webp', category: 'villa', alt: 'Villa bedroom' },
  
  // Beach Bar & Pool images
  { src: '/barefoot1.webp', category: 'beachbar', alt: 'Beach bar' },
  { src: '/dining.webp', category: 'beachbar', alt: 'Pool area' },
  { src: '/coast.webp', category: 'beachbar', alt: 'Beach view' },
  
  // Explore images
  { src: '/mountain.webp', category: 'explore', alt: 'Mountain view' },
  { src: '/village.webp', category: 'explore', alt: 'Local village' },
  { src: '/coast.webp', category: 'explore', alt: 'Coastal view' },
  { src: '/hero-bg.webp', category: 'explore', alt: 'Ocean view' },
];

const FilterBar = ({ activeFilter, onSelect }) => (
  <div className="flex flex-wrap justify-center gap-3 md:gap-6 mb-12">
    {FILTERS.map((filter) => (
      <button
        key={filter.id}
        onClick={() => onSelect(filter.id)}
        className={`px-4 md:px-6 py-2 text-xs md:text-sm uppercase tracking-[0.15em] transition-all duration-300 ${
          activeFilter === filter.id
            ? 'bg-[#D4C5B0] text-black'
            : 'bg-transparent text-black/60 hover:text-black border border-black/20 hover:border-black/40'
        }`}
        data-testid={`filter-${filter.id}`}
      >
        {filter.label}
      </button>
    ))}
  </div>
);

const GalleryGrid = ({ images, onImageClick }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
    {images.map((image, index) => (
      <div
        key={`${image.category}-${image.src}`}
        className="relative aspect-[4/3] overflow-hidden group cursor-pointer"
        data-testid={`gallery-image-${index}`}
        onClick={() => onImageClick(index)}
      >
        <img
          src={image.src}
          alt={image.alt}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
      </div>
    ))}
  </div>
);

const GalleryPage = () => {
  const { t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredImages = activeFilter === 'all' 
    ? GALLERY_IMAGES 
    : GALLERY_IMAGES.filter(img => img.category === activeFilter);

  const lightbox = useLightbox(filteredImages.length);

  return (
    <>
      <div className="bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-[1400px] py-10 md:py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-black mb-6">
              {t('nav.gallery')}
            </h1>
          </div>

          <FilterBar activeFilter={activeFilter} onSelect={setActiveFilter} />
          <GalleryGrid images={filteredImages} onImageClick={lightbox.open} />
        </div>
      </div>

      <Lightbox
        isOpen={lightbox.isOpen}
        onClose={lightbox.close}
        image={filteredImages[lightbox.currentIndex]?.src}
        onPrev={lightbox.prev}
        onNext={lightbox.next}
        hasPrev={filteredImages.length > 1}
        hasNext={filteredImages.length > 1}
      />
    </>
  );
};

export default GalleryPage;
